import { digestAuthRequest } from "./digestAuthRequest"
import xmlParser from "./xml_parser";

// **
// * List available disks on the camera
// * @param {string} cameraIP - IP address of the camera
// * @returns {Promise<Object>} - Response data
// */
export const getNetworkShareDisk = async (cameraIP) => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/disks/list.cgi?diskid=NetworkShare`);
        let parsedResponse = xmlParser.parse(responseData);
        // console.log("[CAMERA UTILITY] Available disks fetched.", responseData)
        return parsedResponse?.root?.disks?.disk;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to fetch available disks.", error)
        return null;
    }
}

// **
// * Add a network share to the camera
// * @param {string} cameraIP - IP address of the camera
// * @param {string} deviceIP - IP address of the PC device
// * @param {string} share - Share name
// * @param {string} user - Username
// * @param {string} nicename - Nicename
// * @param {string} pass - Password
// * @param {string} shareid - Share ID
// * @returns {Promise<Object>} - Response data
// */
export const addNetworkShare = async (cameraIP, deviceIP, share = "carex-storage", user = "na", nicename = "carex-folder", pass = "na", shareid = "777") => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/disks/networkshare/add.cgi?nicename=${nicename}&share=${share}&user=${user}&address=${deviceIP}&pass=${pass}&shareid=${shareid}`);
        let parsedResponse = xmlParser.parse(responseData);
        // console.log("[CAMERA UTILITY] Network share added.", responseData)
        return parsedResponse;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to add network share.", error)
        return null;
    }
}

// **
// * Bind and mount the network share to the camera
// * @param {string} cameraIP - IP address of the camera
// * @param {string} shareid - Share ID
// * @returns {Promise<Object>} - Response data
// */
export const bindAndMountNetworkShare = async (cameraIP, shareid = "777") => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/disks/networkshare/bind.cgi?automaticmount=yes&shareid=${shareid}`);
        let parsedResponse = xmlParser.parse(responseData);
        // console.log("[CAMERA UTILITY] Network share bound and mounted.", responseData)
        return parsedResponse;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to bind and mount network share.", error)
        return null;
    }
}


// **
// * List network shares on the camera. We're assuming only one is returned. DO NOT ATTACH MORE THAN ONE SHARE
// * @param {string} cameraIP - IP address of the camera
// * @param {string} shareid - Share ID
// * @returns {Promise<Object>} - Response data
// */
export const getNetworkShare = async (cameraIP, shareid = "777") => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/disks/networkshare/list.cgi?shareid=${shareid}`);
        let parsedResponse = xmlParser.parse(responseData);
        // console.log("[CAMERA UTILITY] Network share fetched.", responseData)
        return parsedResponse?.NetworkShareResponse?.ListSuccess?.NetworkShares.NetworkShare;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to fetch network share.", error)
        return null;
    }
}


export const deleteNetworkShare = async (cameraIP, shareid = "777") => {
    // First unbind from this url http://carex/axis-cgi/disks/networkshare/unbind.cgi?shareid=777&schemaversion=1
    // Then remove using http://carex/axis-cgi/disks/networkshare/remove.cgi?shareid=777
    try {
        let unbindResponse = await digestAuthRequest(`https://${cameraIP}/axis-cgi/disks/networkshare/unbind.cgi?shareid=${shareid}&schemaversion=1`);
        console.log("[CAMERA UTILITY] Network share unbound.", unbindResponse)

        let removeResponse = await digestAuthRequest(`https://${cameraIP}/axis-cgi/disks/networkshare/remove.cgi?shareid=${shareid}`);
        console.log("[CAMERA UTILITY] Network share removed.", removeResponse)
        return removeResponse;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to remove network share.", error)
        return null;
    }
}

export const setCameraConfigurations = async (cameraIP) => {
    try {

        // action=update&root.Image.I0.Appearance.Resolution=1280x960 for frame rate
        let resolutionResponseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/param.cgi`, 'POST', "action=update&root.Image.I0.Appearance.Resolution=2688x1512");
        console.log(`[CAMERA UTILITY] Camera resolution set to 2688x1512.`, resolutionResponseData)

        let pFrameResponseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/param.cgi`, 'POST', "action=update&root.Image.I0.MPEG.PCount=0");
        console.log(`[CAMERA UTILITY] Camera P-frame count set to 0.`, pFrameResponseData)


        let fpsResponseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/param.cgi`, 'POST', "action=update&root.Image.I0.Stream.FPS=60");
        console.log(`[CAMERA UTILITY] Camera FPS set to 60.`, fpsResponseData)

        let compressionResponseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/param.cgi`, 'POST', "action=update&root.Image.I0.Appearance.Compression=0");
        console.log(`[CAMERA UTILITY] Camera Compression set to 0.`, compressionResponseData)

        let zipStreamResponseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/zipstream/setstrength.cgi?schemaversion=1&strength=off&camera=1`);
        console.log(`[CAMERA UTILITY] Camera Zipstream strength set to off.`, zipStreamResponseData)

        let bitrateResponseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/param.cgi`, 'POST', "action=update&root.Image.I0.RateControl.Mode=mbr");
        console.log(`[CAMERA UTILITY] Camera Bitrate control set to mbr.`, bitrateResponseData)

        return pFrameResponseData && fpsResponseData && compressionResponseData && zipStreamResponseData && bitrateResponseData && resolutionResponseData;

    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to set camera configurations.", error)
        return false;
    }
}


// **
// * Start recording on the camera
// * @param {string} cameraIP - IP address of the camera
// * @param {string} diskid - Disk ID
// * @param {string} options - Options
// * @returns {Promise<Object>} - Response data
// */
export const startRecording = async (cameraIP, diskid = "NetworkShare", options = "resolution%3D2688x1512%26videocodec%3Dh265%26camera%3D1") => {
    // TODO: check if recording is already ongoing.  If it is, stop it first.
    try {
        // Check if there is an ongoing recording first
        let ongoingRecording = await checkForOngoingRecording(cameraIP);
        // If there is, stop it 
        if (ongoingRecording) {
            console.log("[CAMERA UTILITY] Ongoing recording found. Stopping it first.")
            await stopRecording(cameraIP, ongoingRecording.profile);
        }

        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/record/continuous/addconfiguration.cgi?diskid=${diskid}&options=${options}`);
        let parsedResponse = xmlParser.parse(responseData);
        let startSuccessful = parsedResponse?.root?.configure?.result === "OK"

        if (!startSuccessful) {
            console.error("[CAMERA UTILITY] Failed to start recording.", responseData)
            alert("Connected camera is failed to recording. Please check the camera connection.")
            return;
        }

        // console.log("[CAMERA UTILITY] Recording started.", responseData)
        return parsedResponse?.root?.configure?.profile;
    } catch (error) {
        alert("Connected camera is failed to recording. Please check the camera connection.")
        console.error("[CAMERA UTILITY] Failed to start recording.", error)
        return null;
    }
}

// **
// * Stop recording on the camera
// * @param {string} cameraIP - IP address of the camera
// * @param {string} profile - Profile
// * @returns {Promise<Object>} - Response data
// */
export const stopRecording = async (cameraIP, profile) => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/record/continuous/removeconfiguration.cgi?profile=${profile}`);
        let parsedResponse = xmlParser.parse(responseData);
        // console.log("[CAMERA UTILITY] Recording stopped.", responseData)
        return parsedResponse;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to stop recording.", error)
        alert("Connected camera is failed to stop recording. Please check the camera connection.")
        return null;
    }
}

// **
// * Stop recording on the camera
// * @param {string} cameraIP - IP address of the camera
// * @param {string} profile - Profile
// * @returns {Promise<Object>} - Response data
// */
export const createStreamProfile = async (cameraIP, streamProfileName = "carex-profile", description = "Default stream profile", parameters = "videocodec=h265&resolution=2688x1512&fps=60&compression=0&videobitratemode=mbr&videomaxbitrate=0") => {
    try {
        // Check if the stream profile exists first
        let streamProfileExists = await checkIfStreamProfileExists(cameraIP, streamProfileName);

        if (streamProfileExists) {
            console.log("[CAMERA UTILITY] Stream profile already exists.")
            return;
        }

        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/streamprofile.cgi`, 'POST', {
            apiVersion: "1.0",
            method: "create",
            params: {
                streamProfile: [
                    {
                        name: streamProfileName,
                        description: description,
                        parameters: parameters
                    }
                ]
            }
        });

        // console.log("[CAMERA UTILITY] Stream profile created.", responseData);
        return responseData;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to create stream profile.", error)
        return null;
    }
}

// **
// * Check if a stream profile exists on the camera
// * @param {string} cameraIP - IP address of the camera
// * @param {string} streamProfileName - Stream profile name
// * @returns {Promise<Object>} - Response data
// */
const checkIfStreamProfileExists = async (cameraIP, streamProfileName = "carex-profile") => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/streamprofile.cgi`, 'POST', {
            apiVersion: "1.0",
            method: "list",
            params: {
                streamProfileName: []
            }
        });
        // console.log("[CAMERA UTILITY] Stream profiles fetched.", responseData);
        return responseData?.data?.streamProfile?.find(profile => profile.name === streamProfileName);
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to fetch stream profiles.", error)
        return null;
    }
}

// **
// * Check if a recording is ongoing on the camera
// * @param {string} cameraIP - IP address of the camera
// * @returns {Promise<Object>} - Response data
// */
export const checkForOngoingRecording = async (cameraIP) => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/record/continuous/listconfiguration.cgi`);
        let parsedResponse = xmlParser.parse(responseData);
        // console.log("[CAMERA UTILITY] Ongoing recording fetched.", responseData)
        return parsedResponse?.root?.continuousrecordingconfigurations?.continuousrecordingconfiguration;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to fetch ongoing recording.", error)
        return null;
    }
}

// **
// * Check if a camera is connected
// * @param {string} cameraIP - IP address of the camera
// * @returns {Promise<Object>} - Response data
// */
export const checkForAxisCameraConnection = async (cameraIP) => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/param.cgi`);
        // console.log("[CAMERA UTILITY] Camera connection checked.", responseData)
        return responseData;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to check camera connection.", error)
        return null;
    }
}


export const getCameraVideoURL = async (cameraIP) => {
    try {
        // let blob = await digestAuthRequest(`https://${cameraIP}/axis-cgi/mjpg/video.cgi?resolution=1920x1080&compression=25&camera=1&fps=5`);
        let blob = await digestAuthRequest(`https://${cameraIP}/axis-cgi/mjpg/video.cgi`);
        // console.log("[CAMERA UTILITY] Camera video fetched.", blob)
        const blobUrl = URL.createObjectURL(blob);
        return blobUrl;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to fetch camera video.", error)
        return null;
    }
}

export const adjustCameraZoom = async (cameraIP, zoomValue) => {
    try {
        let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/com/ptz.cgi?zoom=${zoomValue}&camera=1`);
        console.log("[CAMERA UTILITY] Camera zoom adjusted.", responseData)
        return responseData;
    } catch (error) {
        console.error("[CAMERA UTILITY] Failed to adjust camera zoom.", error)
        return null;
    }
}

export const checkIfCameraConnected = async (camera, setCameras) => {
    try {
        const response = await checkForAxisCameraConnection(camera.hostname);
        const isConnected = !!response;

        setCameras(prevCameras => prevCameras.map(cam => cam.hostname === camera.hostname ? {
            ...cam,
            connected: isConnected,
            status: isConnected ? 'Camera is connected' : 'Camera is not connected'
        } : cam));

        if (!isConnected) {
            setCameras(prevCameras => prevCameras.map(cam => cam.hostname === camera.hostname ? {
                ...cam,
                connected: false,
                status: 'Failed to connect to camera. Please check the connection try again.'
            } : cam));
        }

    } catch (error) {
        console.error(`Error checking if camera is connected: ${camera.hostname}`, error);
        setCameras(prevCameras => prevCameras.map(cam => cam.hostname === camera.hostname ? {
            ...cam,
            connected: false,
            status: 'Failed to connect to camera. Please check the connection and refresh the page.'
        } : cam));
    }
};
// export const adjustCameraZoom = async (cameraIP, zoomValue) => {
//     try {
//         let responseData = await digestAuthRequest(`https://${cameraIP}/axis-cgi/opticssetup.cgi`, 'POST', `azoom=${zoomValue}&source=1`);
//         console.log("[CAMERA UTILITY] Camera zoom adjusted.", responseData)
//         return responseData;
//     } catch (error) {
//         console.error("[CAMERA UTILITY] Failed to adjust camera zoom.", error)
//         return null;
//     }
// }

